import React, { useState, useEffect } from 'react';
import { Modal } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import './GapGenerationmodal.css';
import axiosPrivate from 'config/axiosPrivate';
import { UserSessionSelector } from 'store/UserSession';
import { fetchAqisisGap, resolveTypixNeoWebsiteParams } from 'helpers/AqisisGapHelper';
import { getLanguageIsoCode, getCountryIsoCode } from 'helpers/ProjectLanguageHelper';
import { transformToSemanticGap } from 'helpers/FormatKeywordsHelper';
import axiosMonetoring from 'config/axiosMonetoring';
import axiosSmartContent from 'config/axiosSmartContent';
import axiosSearchAiOptimization from 'config/axiosSearchAiOptimization';
import { axiosAqisisAPI } from 'config/endpoints';

const setErrorInModal = (setErrorMessage, onLightGapError, message) => {
    setErrorMessage(message || '');
    if (onLightGapError) onLightGapError(message || '');
};

const buildQuery = (obj) =>
    Object.entries(obj)
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
        .join('&');

export const GapGenerationModal = ({
    isOpen,
    onClose,
    mode,
    onModeChange,
    keyword,
    orderWebsite,
    url,
    language,
    country,
    contentScore,
    handleKeywordChange,
    onUpdate,
    onLightGapError,
    disableLightGap = false,
}) => {
    const { t } = useTranslation();
    const { user } = useSelector(UserSessionSelector) || {};
    const [isLoading, setIsLoading] = useState(false);
    const [aqisisHeadKey, setAqisisHeadKey] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [geoDialog, setGeoDialog] = useState(null);
    const [selectedOwnerEmail, setSelectedOwnerEmail] = useState('');

    useEffect(() => {
        if (isOpen) {
            setErrorMessage('');
            setGeoDialog(null);
            setSelectedOwnerEmail('');
            setAqisisHeadKey('');
        }
    }, [isOpen]);

    const clearError = () => setErrorInModal(setErrorMessage, onLightGapError, '');

    const buildGapTextFromOutput = (output) => {
        if (!output || typeof output !== 'string') return '';

        const lines = output
            .split('\n')
            .map((line) => line.replace(/^•\s*/u, '').trim())
            .filter(Boolean);

        const normalizedLines = lines.map((line) => {
            const cleaned = line.replace(/,+\s*$/, '');
            return cleaned;
        });

        return normalizedLines.join('\n');
    };

    const getLanguageCode = () => getLanguageIsoCode(language);
    const getCountryCode = () => getCountryIsoCode(country);

    const findWebsiteFromApi = async () => {
        if (!orderWebsite?.trim()) return null;

        const response = await axiosPrivate.get('/project/website/all');
        const websites = Array.isArray(response?.data) ? response.data : [];
        const normalize = (input) =>
            String(input || '')
                .trim()
                .toLowerCase()
                .replace(/^https?:\/\//, '')
                .replace(/^www\./, '')
                .replace(/\/+$/, '');
        const target = normalize(orderWebsite);

        return (
            websites.find(
                (websiteItem) =>
                    normalize(websiteItem?.urlSite || websiteItem?.url || websiteItem?.name) ===
                    target
            ) || null
        );
    };

    const resolveLightGapLocale = async () => {
        try {
            const match = await findWebsiteFromApi();
            if (match) {
                return {
                    // Prefer language.abbreviation from /project/website/all
                    language: match.language
                        ? getLanguageIsoCode(match.language)
                        : getLanguageCode() || 'fr',
                    country: match.country
                        ? getCountryIsoCode(match.country)
                        : getCountryCode() || 'FR',
                };
            }
        } catch {
            // Fall back to props below
        }

        return {
            language: getLanguageCode() || 'fr',
            country: getCountryCode() || 'FR',
        };
    };

    const extractAllLinksFromContentScore = () => {
        const src = contentScore || {};

        if (Array.isArray(src.all_links)) {
            return src.all_links;
        }
        if (Array.isArray(src.allLinks)) {
            return src.allLinks;
        }
        if (Array.isArray(src.internalLinking?.data?.all_links)) {
            return src.internalLinking.data.all_links;
        }

        return [];
    };

    const transformFoundKeywordFormat = (data) => {
        if (!Array.isArray(data)) return [];

        return data.map((item) => ({
            word: item.keyword || '',
            frequency: item.found || 1,
            insideKeywords: []
        }));
    };

    const transformSearchAiSemanticGap = (data) => {
        if (!Array.isArray(data)) return [];

        return data
            .map((item) => ({
                word: (item?.keyword || item?.word || '').toString().trim(),
                frequency: Number(item?.remainingFrequency ?? item?.frequency ?? item?.found ?? 1),
                insideKeywords: [],
            }))
            .filter((item) => item.word);
    };

    const parseGapResponseData = (responseData) => {
        let gapList = null;
        let gapText = '';

        if (responseData?.PRIMARY && Array.isArray(responseData.PRIMARY) && responseData.PRIMARY.length > 0) {
            gapList = transformFoundKeywordFormat(responseData.PRIMARY);
            gapText = gapList.map(item => `${item.word}(${item.frequency})`).join(',\n');
        } else if (
            Array.isArray(responseData?.semanticGap) &&
            responseData.semanticGap.length > 0 &&
            (responseData.semanticGap[0]?.keyword || responseData.semanticGap[0]?.word)
        ) {
            gapList = transformSearchAiSemanticGap(responseData.semanticGap);
            gapText = gapList.map(item => `${item.word}(${item.frequency})`).join(',\n');
        } else if (
            Array.isArray(responseData) &&
            responseData.length > 0 &&
            responseData[0].hasOwnProperty('found') &&
            responseData[0].hasOwnProperty('keyword')
        ) {
            gapList = transformFoundKeywordFormat(responseData);
            gapText = gapList.map(item => `${item.word}(${item.frequency})`).join(',\n');
        } else {
            const semanticGapRaw = responseData?.semanticGap;
            const rawOutput =
                responseData?.output ||
                responseData?.result ||
                responseData?.gapText ||
                (typeof semanticGapRaw === 'string' ? semanticGapRaw : '') ||
                (Array.isArray(responseData)
                    ? responseData.filter((item) => typeof item === 'string').join('\n')
                    : '') ||
                (typeof responseData === 'string' ? responseData : '');

            gapText = buildGapTextFromOutput(rawOutput);
        }

        return { gapText, gapList };
    };

    const fetchAdminClientEmails = async () => {
        const companyId = user?.company?.id;
        if (!companyId) {
            return [];
        }

        const response = await axiosPrivate.get('/user/admin-client/emails-by-company', {
            params: { companyId },
        });
        const emails = Array.isArray(response?.data) ? response.data : [];

        return emails
            .map((item) => (typeof item === 'string' ? item : item?.mail || item?.email))
            .filter(Boolean);
    };

    const getGeoGapHttpErrorMessage = (error) => {
        const status = error?.response?.status;
        const errorType = error?.response?.data?.type;

        if (status === 404) {
            if (errorType === 'NoWebsiteFound') {
                return t(
                    "Le site web est introuvable dans Search AI. Vérifiez que l'URL configurée dans Typix est identique à celle déclarée dans Search AI, puis réessayez."
                );
            }

            if (errorType === 'NoEmailFound') {
                return t(
                    "Aucune des adresses e-mail des administrateurs Typix n'est associée à un compte Search AI."
                );
            }

            return t(
                'Le site web ou les utilisateurs ne sont pas présents dans Search Ai. Vérifiez que votre compte Search Ai est correctement configuré.'
            );
        }

        if (status === 500) {
            return t(
                'Search Ai est momentanément indisponible. Veuillez réessayer ultérieurement ou contacter le support si le problème persiste.'
            );
        }

        return (
            error?.response?.data?.message ||
            t("Oups....! Une erreur s'est produite lors du calcul du GAP GEO.")
        );
    };

   
    const normalizeSearchAiResponse = (responseData) => {
        if (Array.isArray(responseData)) {
            return responseData.find((item) => item && typeof item === 'object') || null;
        }
        if (responseData && typeof responseData === 'object') {
            return responseData;
        }
        return null;
    };

    const handleSearchAiBusinessStatus = (responseData, requestBody) => {
        const result = normalizeSearchAiResponse(responseData);
        const geoGapStatus = result?.status;

        if (geoGapStatus === 'PROMPT_NOT_FOUND') {
            setGeoDialog({ type: 'PROMPT_NOT_FOUND' });
            return true;
        }

        if (geoGapStatus === 'PAGE_MISMATCH') {
            setGeoDialog({ type: 'PAGE_MISMATCH' });
            return true;
        }

        if (geoGapStatus === 'MULTIPLE_OWNERS') {
            setGeoDialog({
                type: 'MULTIPLE_OWNERS',
                emailOwners: Array.isArray(result?.emailOwners)
                    ? result.emailOwners.filter(Boolean)
                    : [],
                requestBody,
            });
            setSelectedOwnerEmail('');
            return true;
        }

        return false;
    };

    const executeGeoGapRequest = async (requestBody) => {
        const response = await axiosSearchAiOptimization.post(
            '/optimization/find_by_prompt',
            requestBody
        );

        if (handleSearchAiBusinessStatus(response?.data, requestBody)) {
            return;
        }

        const result = normalizeSearchAiResponse(response?.data);
        const { gapText, gapList } = parseGapResponseData(result ?? response?.data);

        applyGapResult(gapText, gapList);
        setGeoDialog(null);

        if (typeof onClose === 'function') {
            onClose();
        }
    };

    const handleGeoOwnerConfirm = async () => {
        if (!selectedOwnerEmail || !geoDialog?.requestBody || isLoading) return;

        setIsLoading(true);
        try {
            clearError();

            const requestBody = {
                ...geoDialog.requestBody,
                emails: [selectedOwnerEmail],
            };

            await executeGeoGapRequest(requestBody);
        } catch (error) {
            console.error('Erreur lors de la récupération du GAP GEO (Search Ai):', error);

            const businessData = error?.response?.data;
            if (businessData && handleSearchAiBusinessStatus(businessData, geoDialog.requestBody)) {
                return;
            }

            setErrorInModal(setErrorMessage, onLightGapError, getGeoGapHttpErrorMessage(error));
        } finally {
            setIsLoading(false);
        }
    };

    const applyGapResult = (gapText, gapListFromApi = null) => {
        let gapList = gapListFromApi;

        if (!gapList && gapText) {
            gapList = transformToSemanticGap(gapText || '');
        }

        if (gapText && typeof handleKeywordChange === 'function') {
            handleKeywordChange({ target: { value: gapText } });
        }

        if (Array.isArray(gapList) && gapList.length > 0 && typeof onUpdate === 'function') {
            onUpdate(gapList);
        }
    };

    const runLightGap = async () => {
        if (!keyword || !keyword.trim() || !orderWebsite || !orderWebsite.trim()) {
            setErrorInModal(
                setErrorMessage,
                onLightGapError,
                t('Veuillez renseigner un mot-clé et un site web avant de lancer le Light GAP.')
            );
            return;
        }

        try {
            clearError();

            const { language: resolvedLanguage, country: resolvedCountry } =
                await resolveLightGapLocale();

            const { website: resolvedWebsite } = resolveTypixNeoWebsiteParams(
                orderWebsite
            );

            const requestBody = {
                keyword: keyword.trim(),
                website: resolvedWebsite,
                language: resolvedLanguage,
                country: resolvedCountry,
            };

            const response = await axiosSmartContent.post(
                `/smart-content/smart_content/semantic_gap`,
                requestBody
            );

            const { gapText, gapList } = parseGapResponseData(response?.data);

            applyGapResult(gapText, gapList);

            if (typeof onClose === 'function') {
                onClose();
            }
        } catch (error) {
            console.error('Erreur lors de la récupération du Light GAP (semanticStage):', error);
            setErrorInModal(
                setErrorMessage,
                onLightGapError,
                t(
                    'Une erreur est survenue lors de la génération du gap. Veuillez réessayer dans quelques instants. Si le problème persiste, contactez le support.'
                )
            );
        }
    };

    const runGeoGap = async () => {
        if (!keyword || !keyword.trim() || !orderWebsite || !orderWebsite.trim()) {
            setErrorInModal(
                setErrorMessage,
                onLightGapError,
                t('Veuillez renseigner un mot-clé et un site web avant de lancer le GAP GEO.')
            );
            return;
        }

        let requestBody = null;

        try {
            clearError();

            const emails = await fetchAdminClientEmails();

            requestBody = {
                website: orderWebsite.trim(),
                questions: [
                    {
                        question: keyword.trim(),
                        page: '',
                    },
                ],
                emails,
            };

            await executeGeoGapRequest(requestBody);
        } catch (error) {
            console.error('Erreur lors de la récupération du GAP GEO (Search Ai):', error);

            const businessData = error?.response?.data;
            if (businessData && requestBody && handleSearchAiBusinessStatus(businessData, requestBody)) {
                return;
            }

            setErrorInModal(setErrorMessage, onLightGapError, getGeoGapHttpErrorMessage(error));
        }
    };

    const runAqisisGap = async () => {
        clearError();

        // Si aqisisHeadKey est rempli :
        //   → keyword = aqisisHeadKey (mot clé principal Aqisis)
        //   → headkeys = keyword (mot clé du formulaire)
        // Sinon :
        //   → keyword = keyword uniquement (sans headkeys)
        const aqisisParams = aqisisHeadKey.trim()
            ? { keyword: aqisisHeadKey.trim(), headkeys: keyword }
            : { keyword };

        await fetchAqisisGap({
            ...aqisisParams,
            orderWebsite,
            url: url || '',
            onError: (msg) => setErrorInModal(setErrorMessage, onLightGapError, msg),
            onSuccess: ({ gapText, gapList }) => {
                if (gapText && typeof handleKeywordChange === 'function') {
                    handleKeywordChange({ target: { value: gapText } });
                }

                if (
                    Array.isArray(gapList) &&
                    gapList.length > 0 &&
                    typeof onUpdate === 'function'
                ) {
                    onUpdate(gapList);
                }

                if (typeof onClose === 'function') {
                    onClose();
                }
            },
            onKeywordNotFound: () => {
                setErrorInModal(
                    setErrorMessage,
                    onLightGapError,
                    t(
                        "Ce mot clé n'existe pas sur AQISIS, veuillez vérifier avec le support Typix : support-typix@pixalione.com"
                    )
                );
            },
            onWebsiteNotFound: () => {
                setErrorInModal(
                    setErrorMessage,
                    onLightGapError,
                    t(
                        "Ce siteweb n'existe pas sur AQISIS, veuillez vérifier avec le support Typix : support-typix@pixalione.com"
                    )
                );
            },
            onValidationError: () => {
                setErrorInModal(
                    setErrorMessage,
                    onLightGapError,
                    t(
                        "Le gap sémantique de ce mot clé n'a pas encore été validé, veuillez contacter le support Typix : support-typix@pixalione.com"
                    )
                );
            },
            setLoading: setIsLoading,
            t,
        });
    };
    const handleGenerateClick = async () => {
        if (isLoading) return;

        setIsLoading(true);
        try {
            if (mode === 'aqisis') {
                await runAqisisGap();
            } else if (mode === 'geo') {
                await runGeoGap();
            } else {
                await runLightGap();
            }
        } finally {
            setIsLoading(false);
        }
    };

    const getGeoDialogMessage = () => {
        if (!geoDialog) return '';

        switch (geoDialog.type) {
            case 'PROMPT_NOT_FOUND':
                return t(
                    "Le prompt n'existe pas dans Search Ai. Veuillez vérifier que le mot-clé correspond bien au prompt recherché."
                );
            case 'PAGE_MISMATCH':
                return t(
                    "Aucune correspondance entre l'URL et la question n'a été trouvée dans Search Ai."
                );
            case 'MULTIPLE_OWNERS':
                return t(
                    "Ce prompt est associé à plusieurs analyses Search Ai. Sélectionnez l'adresse e-mail correspondant à l'analyse depuis laquelle vous souhaitez récupérer le gap sémantique."
                );
            default:
                return '';
        }
    };

    return (
        <>
            <Modal
                isOpen={isOpen}
                toggle={onClose}
                className="gap-generation-modal"
                backdrop={true}
                keyboard={true}
                centered
            >
                <div className="gap-generation-modal-content">
                    <div className="gap-generation-modal-header">
                        <div>
                            <h2 className="gap-generation-modal-title">
                                {t('Génération de gap sémantique')}
                            </h2>
                            <p className="gap-generation-modal-subtitle">
                                {t(
                                    'Identifiez les concepts manquants autour de votre mot-clé pour améliorer la pertinence SEO du texte.'
                                )}
                            </p>
                        </div>
                        <button
                            className="gap-generation-modal-close"
                            onClick={onClose}
                            aria-label={t('Fermer')}
                        >
                            ×
                        </button>
                    </div>

                    <div className="gap-generation-modal-body">
                        {errorMessage && (
                            <div className="gap-generation-modal-error" role="alert">
                                <span className="gap-generation-modal-error-text">{errorMessage}</span>
                                <button
                                    type="button"
                                    className="gap-generation-modal-error-dismiss"
                                    onClick={clearError}
                                    aria-label={t('Fermer')}
                                >
                                    ×
                                </button>
                            </div>
                        )}
                        <button
                            type="button"
                            className={`gap-option-card ${mode === 'aqisis' ? 'gap-option-card--active' : ''}`}
                            onClick={() => {
                                clearError();
                                onModeChange('aqisis');
                            }}
                        >
                            <div className="gap-option-radio">
                                <span
                                    className={mode === 'aqisis' ? 'radio-dot--active' : 'radio-dot'}
                                />
                            </div>
                            <div className="gap-option-content">
                                <div className="gap-option-title-row">
                                    <span className="gap-option-title">{t('Rapide - via Aqisis')}</span>
                                </div>
                                <p className="gap-option-description">
                                    {t(
                                        'Analyse basée sur la base de données Aqisis. Requiert un accès actif à Aqisis.'
                                    )}
                                </p>

                                {mode === 'aqisis' && (
                                    <div
                                        className="gap-aqisis-headkey-wrapper"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <label
                                            htmlFor="aqisis-headkey-input"
                                            className="gap-aqisis-headkey-label"
                                        >
                                            {t('Mot clé principal dans Aqisis')}
                                            <span className="gap-aqisis-headkey-optional">
                                                {' '}
                                                ({t('optionnel')})
                                            </span>
                                        </label>
                                        <input
                                            id="aqisis-headkey-input"
                                            type="text"
                                            className="gap-aqisis-headkey-input"
                                            placeholder={t('Ex : référencement naturel')}
                                            value={aqisisHeadKey}
                                            onChange={(e) => setAqisisHeadKey(e.target.value)}
                                        />
                                    </div>
                                )}
                            </div>
                        </button>

                        <button
                            type="button"
                            className={`gap-option-card ${mode === 'geo' ? 'gap-option-card--active' : ''}`}
                            onClick={() => {
                                clearError();
                                onModeChange('geo');
                            }}
                        >
                            <div className="gap-option-radio">
                                <span
                                    className={mode === 'geo' ? 'radio-dot--active' : 'radio-dot'}
                                />
                            </div>
                            <div className="gap-option-content">
                                <div className="gap-option-title-row">
                                    <span className="gap-option-title">
                                        {t('GEO - Via Search Ai')}
                                    </span>
                                    <span className="gap-badge-searchai">{t('Search Ai')}</span>
                                </div>
                                <p className="gap-option-description">
                                    {t(
                                        'Récupération du gap recommandé par Search Ai pour le GEO. Requiert un accès Search Ai actif.'
                                    )}
                                </p>
                            </div>
                        </button>

                        <button
                            type="button"
                            className={`gap-option-card ${mode === 'light' ? 'gap-option-card--active' : ''} ${disableLightGap ? 'gap-option-card--disabled' : ''}`}
                            onClick={
                                disableLightGap
                                    ? undefined
                                    : () => {
                                        clearError();
                                        onModeChange('light');
                                    }
                            }
                            disabled={disableLightGap}
                        >
                            <div className="gap-option-radio">
                                <span
                                    className={
                                        mode === 'light' && !disableLightGap
                                            ? 'radio-dot--active'
                                            : 'radio-dot'
                                    }
                                />
                            </div>
                            <div className="gap-option-content">
                                <div className="gap-option-title-row">
                                    <span className="gap-option-title">
                                        {t('Analyse intelligente (Light Gap)')}
                                    </span>
                                </div>
                                <p className="gap-option-description">
                                    {t(
                                        'Génération basée sur votre contenu et votre mot-clé. Peut prendre quelques secondes.'
                                    )}
                                </p>
                            </div>
                        </button>
                    </div>

                    <div className="gap-generation-modal-footer">
                        <button type="button" className="gap-generation-cancel" onClick={onClose}>
                            {t('Annuler')}
                        </button>
                        <button
                            type="button"
                            className={`gap-generation-confirm${isLoading ? ' gap-generation-confirm--loading' : ''}`}
                            onClick={handleGenerateClick}
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <>
                                    <span className="gap-generation-spinner" aria-hidden="true" />
                                    {t('Génération...')}
                                </>
                            ) : (
                                t('Générer')
                            )}
                        </button>
                    </div>
                </div>
            </Modal>

            <Modal
                isOpen={Boolean(geoDialog)}
                toggle={() => setGeoDialog(null)}
                className="gap-geo-dialog-modal"
                backdrop={true}
                keyboard={true}
                centered
            >
                <div className="gap-geo-dialog-content">
                    <div className="gap-geo-dialog-header">
                        <h3 className="gap-geo-dialog-title">{t('Search Ai')}</h3>
                        <button
                            type="button"
                            className="gap-generation-modal-close"
                            onClick={() => setGeoDialog(null)}
                            aria-label={t('Fermer')}
                        >
                            ×
                        </button>
                    </div>

                    <div className="gap-geo-dialog-body">
                        <p className="gap-geo-dialog-message">{getGeoDialogMessage()}</p>

                        {geoDialog?.type === 'MULTIPLE_OWNERS' && (
                            <div className="gap-geo-owner-list" role="radiogroup" aria-label={t('Sélectionner une adresse e-mail')}>
                                {geoDialog.emailOwners.map((email) => (
                                    <label key={email} className="gap-geo-owner-option">
                                        <input
                                            type="radio"
                                            name="geo-owner-email"
                                            value={email}
                                            checked={selectedOwnerEmail === email}
                                            onChange={() => setSelectedOwnerEmail(email)}
                                        />
                                        <span className="gap-geo-owner-email">{email}</span>
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="gap-geo-dialog-footer">
                        {geoDialog?.type === 'MULTIPLE_OWNERS' ? (
                            <>
                                <button
                                    type="button"
                                    className="gap-generation-cancel"
                                    onClick={() => setGeoDialog(null)}
                                >
                                    {t('Annuler')}
                                </button>
                                <button
                                    type="button"
                                    className={`gap-generation-confirm${isLoading ? ' gap-generation-confirm--loading' : ''}`}
                                    onClick={handleGeoOwnerConfirm}
                                    disabled={!selectedOwnerEmail || isLoading}
                                >
                                    {isLoading ? (
                                        <>
                                            <span className="gap-generation-spinner" aria-hidden="true" />
                                            {t('Génération...')}
                                        </>
                                    ) : (
                                        t('Générer')
                                    )}
                                </button>
                            </>
                        ) : (
                            <button
                                type="button"
                                className="gap-generation-confirm"
                                onClick={() => setGeoDialog(null)}
                            >
                                {t('Fermer')}
                            </button>
                        )}
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default GapGenerationModal;